<template>
    <div class="time-range-facet-container">
        <span
            class="facet-title"
            v-if="isTimePicker"
        >{{ facetName }}</span>
        <div class="time-range-facet">
            <div class="time-inputs">
                <!-- Start Time -->
                <div class="time-field">
                    <div class="advanced-filter-input-group-time-select">
                        <input
                            ref="startHoursInputRef"
                            type="text"
                            inputmode="numeric"
                            pattern="[0-9]*"
                            min="1"
                            max="12"
                            maxlength="2"
                        
                            :value="startHours"
                            :disabled="isLoading"
                            @input="handleHoursInputSmart('start', $event)"
                            @blur="validateHours('start')"
                        >
                        <span class="advanced-filter-time-select-seperator">:</span>
                        <input
                            ref="startMinutesInputRef"
                            type="text"
                            inputmode="numeric"
                            pattern="[0-9]*"
                            min="0"
                            max="59"
                            maxlength="2"
                            :value="startMinutesDisplay"
                            :disabled="isLoading"
                            @input="handleInput('startMinutes', $event)"
                            @blur="validateMinutes('start')"
                        >
                        <select
                            ref="startPeriodRef"
                            v-model="startPeriod"
                            :disabled="isLoading"
                            @change="onChangeTime"
                            @keydown="handleSelectKeydown($event, 'start')"
                        >
                            <option>AM</option>
                            <option>PM</option>
                        </select>
                    </div>
                </div>

                <span class="to-label">to</span>

                <!-- End Time -->
                <div class="time-field">
                    <div class="advanced-filter-input-group-time-select">
                        <input
                            ref="endHoursInputRef"
                            type="text"
                            inputmode="numeric"
                            pattern="[0-9]*"
                            min="1"
                            max="12"
                            maxlength="2"
                            :value="endHours"
                            :disabled="isLoading"
                            @input="handleHoursInputSmart('end', $event)"
                            @blur="validateHours('end')"
                        >
                        <span class="advanced-filter-time-select-seperator">:</span>
                        <input
                            ref="endMinutesInputRef"
                            type="text"
                            inputmode="numeric"
                            pattern="[0-9]*"
                            min="0"
                            max="59"
                            maxlength="2"
                            :value="endMinutesDisplay"
                            :disabled="isLoading"
                            @input="handleInput('endMinutes', $event)"
                            @blur="validateMinutes('end')"
                        >
                        <select
                            ref="endPeriodRef"
                            v-model="endPeriod"
                            :disabled="isLoading"
                            @change="onChangeTime"
                            @keydown="handleSelectKeydown($event, 'end')"
                        >
                            <option>AM</option>
                            <option>PM</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import {
    computed, inject, onMounted, ref
} from 'vue';
import { tz } from 'moment-timezone';
import type { InjectedPagePreferences } from '@agyletime/vue-3-common/src/typings/pagePreference';
import { useSelectedViewFacet } from '@/components/TeamAndSchedule/composables/useSelectedViewFacet';
import { useTimeRange } from '@/components/TeamAndSchedule/composables/useTimeRange';

const emit = defineEmits(['change']);

const props = defineProps<{
  facetId: string;
  facetName: string;
  urlKey?: string;
  isLoading: boolean;
  activeTable: string;
}>();

const isTimePicker = computed(() => {
    return props?.facetId
        ? props.facetId.includes('tasksStartFacet') || props.facetId.includes('shiftStartFacet')
        : false;
});

const pagePreferences = inject<InjectedPagePreferences>('pagePreferences');
const resetFilters = inject('resetFilters', ref(false));
const userTimezone = inject('userTimezone', ref(tz.guess()));

// Use the time range composable
const {
    startHours,
    startMinutes: _startMinutes,
    startPeriod,
    endHours,
    endMinutes: _endMinutes,
    endPeriod,
    startMinutesDisplay,
    endMinutesDisplay,
    startMinutesInputRef,
    endMinutesInputRef,
    handleInput,
    handleHoursInputSmart,
    validateHours,
    validateMinutes,
    onChangeTime,
    loadTimeRangeFromUrl,
    onSelectedViewFacetChange
} = useTimeRange({
    facetId: props.facetId,
    facetName: props.facetName,
    urlKey: props.urlKey,
    activeTable: props.activeTable,
    userTimezone,
    emit
});

onMounted(() => loadTimeRangeFromUrl());

useSelectedViewFacet({
    pagePreferences,
    activeTable: props.activeTable,
    facetId: props.facetId,
    resetFilters,
    onSelectedViewFacetChange
});

// Refs for AM/PM select elements
const startPeriodRef = ref<HTMLSelectElement | null>(null);
const endPeriodRef = ref<HTMLSelectElement | null>(null);

// Helper to open select dropdown
const openSelectDropdown = (selectElement: HTMLSelectElement) => {
    try {
        // Try modern showPicker API first (Chrome 99+, Safari 16+)
        if ('showPicker' in selectElement) {
            (selectElement as HTMLSelectElement & { showPicker: () => void }).showPicker();
        } else {
            (selectElement as HTMLSelectElement).click();
        }
    } catch {
        // Fallback if showPicker fails
        (selectElement as HTMLSelectElement).click();
    }
};

// Keyboard handler for AM/PM dropdowns
const handleSelectKeydown = (event: KeyboardEvent, type: 'start' | 'end') => {
    if (event.key !== 'Enter') return;
    
    event.preventDefault();
    const selectElement = type === 'start' ? startPeriodRef.value : endPeriodRef.value;
    
    if (selectElement && !selectElement.disabled) {
        openSelectDropdown(selectElement);
    }
};
</script>

<style scoped>
.time-range-facet-container {
  width: 100%;
}

.facet-title {
  font-family: Roboto;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0%;
  vertical-align: middle;
  color: #1A2021;
}

.time-range-facet {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 4px;
  font-family: Roboto;
}

.facet-label {
  font-weight: 500;
  margin-bottom: 0;
  font-family: Roboto;
  font-size: 14px;
  font-weight: 400;
  font-style: normal;
  line-height: 20px;
  letter-spacing: 0%;
  vertical-align: middle;
  color: #1A2021;
}

.time-inputs {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.time-field {
  display: flex;
  flex-direction: column;
}

.time-field:first-child {
  margin-bottom: 16px;
}

/* Angular-style time picker - Figma specs */
.advanced-filter-input-group-time-select {
    display: inline-flex;
    border: 1px solid #DCE3E4;
    border-radius: 4px;
    overflow: hidden;
    height: 28px;
    width: 165px;
    opacity: 1;
    align-items: center;
}

.advanced-filter-input-group-time-select input {
    width: 42px;
    padding: 0 6px;
    text-align: center;
    border: none;
    height: 100%;
    font-size: 14px;
    font-family: Roboto;
    outline: none;
    color: #1A2021;
    flex-shrink: 0;
}

.advanced-filter-input-group-time-select input::placeholder {
    color: #9E9E9E;
    opacity: 1;
}

.advanced-filter-input-group-time-select input:focus {
    background-color: #F5F5F5;
}

.advanced-filter-input-group-time-select input::-webkit-outer-spin-button,
.advanced-filter-input-group-time-select input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

/* Firefox */
.advanced-filter-input-group-time-select input[type=number] {
    -moz-appearance: textfield;
    appearance: textfield;
}

.advanced-filter-input-group-time-select select {
    width: 52px;
    height: 28px;
    padding: 0 2px 0 8px;
    margin-left: 0;
    border: none;
    border-left: 1px solid #DCE3E4;
    outline: none;
    background: white;
    font-family: Roboto;
    font-weight: 400;
    font-size: 14px;
    line-height: 24px;
    letter-spacing: 0%;
    vertical-align: middle;
    color: #1A2021;
    cursor: pointer;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M7 10l5 5 5-5' stroke='%231A2021' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 2px center;
    background-size: 20px 20px;
    flex-shrink: 0;
}

/* Only show focus outline for keyboard navigation on AM/PM select */
.advanced-filter-input-group-time-select select:focus-visible {
    outline: 2px solid #000;
    outline-offset: -2px;
}

.advanced-filter-input-group-time-select select option {
    font-family: Roboto;
    font-weight: 400;
    font-size: 14px;
    line-height: 24px;
    letter-spacing: 0%;
    color: #1A2021;
}

.advanced-filter-time-select-seperator {
    display: flex;
    align-items: center;
    font-size: 14px;
    font-family: Roboto;
    color: #1A2021;
    padding: 0 4px 0 17px;
    flex-shrink: 0;
}

.to-label {
  font-family: Roboto;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  margin-top: 0;
  margin-bottom: 4px;
  display: block;
  color: #1A2021;
}

.facet-title {
  padding-bottom: 0px;
  display: block;
  font-family: Roboto;
  font-size: 14px;
  font-weight: 400;
  font-style: normal;
  line-height: 20px;
  letter-spacing: 0%;
  color: #1A2021;
}

/* Disabled state */
.advanced-filter-input-group-time-select input:disabled,
.advanced-filter-input-group-time-select select:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
    opacity: 0.6;
}
</style>
